import { memo, useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useSessionContext } from "@/contexts/SessionContext";
import { buildPatientQuestionnaireSigninLink } from "src-next/shared/patientQuestionnaireLink";

import type { QuestionnairePayload } from "../../form-builder/types";
import { useQuestionnaireManagement } from "../../form-builder/hooks";
import {
  filterQuestionnaires,
  type QuestionnaireCategoryFilter,
  type QuestionnaireStatusFilter,
} from "../utils/questionnaireManagementFilterUtils";
import ConfirmDeleteQuestionnaireDialog from "./ConfirmDeleteQuestionnaireDialog";
import QuestionnaireManagementList from "./QuestionnaireManagementList";
import QuestionnaireManagementTabsHeader from "./QuestionnaireManagementTabsHeader";
import QuestionnairePreviewDialog from "./QuestionnairePreviewDialog";
import { oids } from "@/configs/Settings";

export interface QuestionnaireManagementTabsPanelSectionProps {
  organizationId: string;
  onEditQuestionnaire: (questionnaireId: string) => void;
  onCreateQuestionnaire: () => void;
}

const QuestionnaireManagementTabsPanelSection = memo(
  ({
    organizationId,
    onEditQuestionnaire,
    onCreateQuestionnaire,
  }: QuestionnaireManagementTabsPanelSectionProps) => {
    const { access, user } = useSessionContext();

    const {
      questionnaires,
      isLoading,
      errorMessage,
      deleteQuestionnaire,
      isDeleting,
      deleteErrorMessage,
    } = useQuestionnaireManagement({
      organizationId: `${oids.cnpj}-${organizationId}`,
    });

    const [questionnaireForPreview, setQuestionnaireForPreview] =
      useState<QuestionnairePayload | null>(null);
    const [questionnaireIdPendingDelete, setQuestionnaireIdPendingDelete] =
      useState<string | null>(null);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [copyLinkSnackbarOpen, setCopyLinkSnackbarOpen] = useState(false);
    const [copyLinkSnackbarSeverity, setCopyLinkSnackbarSeverity] = useState<
      "success" | "warning"
    >("success");
    const [copyLinkSnackbarMessage, setCopyLinkSnackbarMessage] = useState("");

    const practitionerRoleReference = useMemo(() => {
      const username = user?.username;
      if (!access?.organizationId || typeof username !== "string") return "";
      return `PractitionerRole/${access.organizationId}-${oids.cpf}-${username}`;
    }, [access?.organizationId, user?.username]);

    const ownerOrganizationReference = useMemo(
      () => `Organization/${oids.cnpj}-${organizationId}`,
      [organizationId],
    );

    const questionnaireLinkNotifierContext = useMemo(() => {
      if (
        access?.type !== "professional" ||
        !practitionerRoleReference ||
        !ownerOrganizationReference
      ) {
        return null;
      }
      return {
        practitionerRoleReference,
        ownerOrganizationReference,
      };
    }, [access?.type, ownerOrganizationReference, practitionerRoleReference]);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] =
      useState<QuestionnaireStatusFilter>("all");
    const [categoryFilter, setCategoryFilter] =
      useState<QuestionnaireCategoryFilter>("all");

    const filteredQuestionnaires = useMemo(
      () =>
        filterQuestionnaires(questionnaires, {
          searchQuery,
          statusFilter,
          categoryFilter,
        }),
      [questionnaires, searchQuery, statusFilter, categoryFilter],
    );

    const questionnairePendingDeleteTitle = useMemo(() => {
      if (!questionnaireIdPendingDelete) return undefined;
      return questionnaires.find((q) => q.id === questionnaireIdPendingDelete)
        ?.title;
    }, [questionnaires, questionnaireIdPendingDelete]);

    const handleClosePreview = useCallback(() => {
      setQuestionnaireForPreview(null);
    }, []);

    const handleOpenDelete = useCallback((questionnaireId: string) => {
      setQuestionnaireIdPendingDelete(questionnaireId);
    }, []);

    const handleCloseDelete = useCallback(() => {
      setQuestionnaireIdPendingDelete(null);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
      if (!questionnaireIdPendingDelete) return;
      await deleteQuestionnaire({
        questionnaireId: questionnaireIdPendingDelete,
      });
      setQuestionnaireIdPendingDelete(null);
    }, [deleteQuestionnaire, questionnaireIdPendingDelete]);

    const handleCopyPatientLink = useCallback(
      async (questionnaireId: string) => {
        const url = questionnaireLinkNotifierContext
          ? buildPatientQuestionnaireSigninLink(questionnaireId, {
              notifierContext: questionnaireLinkNotifierContext,
            })
          : buildPatientQuestionnaireSigninLink(questionnaireId);

        const hasNotifier = Boolean(questionnaireLinkNotifierContext);

        try {
          await navigator.clipboard.writeText(url);
          setCopyLinkSnackbarSeverity(hasNotifier ? "success" : "warning");
          setCopyLinkSnackbarMessage(
            hasNotifier
              ? "Link copiado"
              : "Link copiado sem identificação do profissional (sessão incompleta ou perfil não profissional). O paciente poderá responder, mas a notificação automática pode não ser criada.",
          );
          setCopyLinkSnackbarOpen(true);
        } catch {
          try {
            window.prompt("Copie o link do paciente:", url);
          } catch {
            /* ignore */
          }
        }
      },
      [questionnaireLinkNotifierContext],
    );

    useEffect(() => {
      if (filteredQuestionnaires.length === 0) {
        if (activeId !== null) setActiveId(null);
        return;
      }

      const isStillPresent = activeId
        ? filteredQuestionnaires.some((q) => q.id === activeId)
        : false;
      if (isStillPresent) return;

      setActiveId(filteredQuestionnaires[0]?.id ?? null);
    }, [activeId, filteredQuestionnaires]);

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <QuestionnaireManagementTabsHeader
              searchQuery={searchQuery}
              statusFilter={statusFilter}
              categoryFilter={categoryFilter}
              onChangeSearchQuery={setSearchQuery}
              onChangeStatusFilter={setStatusFilter}
              onChangeCategoryFilter={setCategoryFilter}
              onCreateQuestionnaire={onCreateQuestionnaire}
            />

            <QuestionnaireManagementList
              questionnaires={filteredQuestionnaires}
              isLoading={isLoading}
              errorMessage={errorMessage}
              activeId={activeId}
              isDeleting={isDeleting}
              emptyMessage="Nenhum questionário encontrado com os filtros aplicados."
              onSelectQuestionnaire={setActiveId}
              onEditQuestionnaire={onEditQuestionnaire}
              onOpenPreview={setQuestionnaireForPreview}
              onOpenDelete={handleOpenDelete}
              onCopyPatientLink={handleCopyPatientLink}
            />
          </Stack>
        </CardContent>

        {questionnaireForPreview && (
          <QuestionnairePreviewDialog
            open={Boolean(questionnaireForPreview)}
            questionnaire={questionnaireForPreview}
            onClose={handleClosePreview}
          />
        )}

        {questionnaireIdPendingDelete && (
          <ConfirmDeleteQuestionnaireDialog
            open={Boolean(questionnaireIdPendingDelete)}
            title={questionnairePendingDeleteTitle}
            isConfirming={isDeleting}
            errorMessage={deleteErrorMessage}
            onClose={handleCloseDelete}
            onConfirm={handleConfirmDelete}
          />
        )}

        <Snackbar
          open={copyLinkSnackbarOpen}
          autoHideDuration={copyLinkSnackbarSeverity === "warning" ? 8000 : 4000}
          onClose={() => setCopyLinkSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setCopyLinkSnackbarOpen(false)}
            severity={copyLinkSnackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {copyLinkSnackbarMessage || "Link copiado"}
          </Alert>
        </Snackbar>
      </Card>
    );
  },
);

QuestionnaireManagementTabsPanelSection.displayName =
  "QuestionnaireManagementTabsPanelSection";

export default QuestionnaireManagementTabsPanelSection;
